// src/components/NetBotWidget.jsx — v2 : Markdown + Suggestions + Contexte enrichi
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton,
  CircularProgress, Chip, Collapse, Tooltip, Select,
  MenuItem, FormControl, Divider, Stack,
} from '@mui/material';
import SendIcon          from '@mui/icons-material/Send';
import SmartToyIcon      from '@mui/icons-material/SmartToy';
import CloseIcon         from '@mui/icons-material/Close';
import RestartAltIcon    from '@mui/icons-material/RestartAlt';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import { sendNetbotMessage, getNetbotProviders } from '../api/netbotApi';

const MODES = [
  { value: 'lab_debugger',   label: '🔧 Debugger Lab',   color: '#0f766e' },
  { value: 'course_tutor',   label: '📚 Tuteur Cours',   color: '#1d4ed8' },
  { value: 'exam_assistant', label: '🎯 Assistant Exam', color: '#7c3aed' },
];

// Suggestions rapides par mode
const SUGGESTIONS = {
  lab_debugger: [
    'Mon OSPF ne converge pas',
    'Comment vérifier les voisins BGP ?',
    'Ping fonctionne mais pas le routage',
    'Expliquer show ip route',
  ],
  course_tutor: [
    'Expliquer le protocole STP',
    'Différence OSPF vs EIGRP ?',
    'Comment fonctionne le subnetting ?',
    'Qu\'est-ce que le NAT ?',
  ],
  exam_assistant: [
    'Indice sur cette topologie',
    'Suis-je dans la bonne direction ?',
    'Rappel syntaxe commande',
    'Vérifier ma compréhension',
  ],
};

// Rendu Markdown simple (code blocks + gras + italique)
function MarkdownText({ content }) {
  if (!content) return null;

  const parts = content.split(/(```[\s\S]*?```|`[^`]+`)/g);

  return (
    <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {parts.map((part, i) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          const code = part.slice(3, -3).replace(/^[a-z]*\n/, '');
          return (
            <Box key={i} sx={{ position: 'relative', mt: 1, mb: 1 }}>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#0f172a', color: '#e2e8f0',
                  p: 1.5, borderRadius: 1.5, fontSize: 11,
                  overflowX: 'auto', m: 0, fontFamily: 'monospace',
                  border: '1px solid rgba(255,255,255,.08)',
                }}
              >
                {code}
              </Box>
              <Tooltip title="Copier">
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(code)}
                  sx={{
                    position: 'absolute', top: 4, right: 4,
                    color: '#94a3b8', p: 0.5,
                    '&:hover': { color: '#e2e8f0' },
                  }}
                >
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return (
            <Box
              key={i} component="code"
              sx={{
                bgcolor: '#f1f5f9', color: '#0f766e',
                px: 0.5, py: 0.25, borderRadius: 0.5,
                fontSize: '0.85em', fontFamily: 'monospace',
              }}
            >
              {part.slice(1, -1)}
            </Box>
          );
        }
        // Gras **text**
        return part.split(/(\*\*[^*]+\*\*)/g).map((p, j) => {
          if (p.startsWith('**') && p.endsWith('**')) {
            return <Box key={j} component="strong">{p.slice(2, -2)}</Box>;
          }
          return <span key={j}>{p}</span>;
        });
      })}
    </Box>
  );
}

export default function NetBotWidget({ defaultMode = 'lab_debugger', context = '' }) {
  const [open,      setOpen]      = useState(false);
  const [messages,  setMessages]  = useState([]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [mode,      setMode]      = useState(defaultMode);
  const [enabled,   setEnabled]   = useState(true);
  const [showSugg,  setShowSugg]  = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    getNetbotProviders()
      .then(r => setEnabled(r.data?.enabled !== false))
      .catch(() => setEnabled(false));
  }, []);

  useEffect(() => {
    if (open && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Masquer suggestions dès qu'il y a des messages
  useEffect(() => {
    setShowSugg(messages.length === 0);
  }, [messages]);

  const currentMode = MODES.find(m => m.value === mode) || MODES[0];

  const send = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMsg = { role: 'user', content: msg };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput('');
    setLoading(true);

    try {
      // Construire le contexte enrichi
      const contextObj = context ? { lab_info: context } : {};

      const r = await sendNetbotMessage(mode, history, contextObj);
      setMessages([...history, { role: 'assistant', content: r.data.reply }]);
    } catch (e) {
      setMessages([...history, {
        role: 'assistant',
        content: e?.response?.data?.detail?.message || 'Erreur NetBot. Reessayez.',
        error: true,
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, mode, context]);

  if (!enabled) return null;

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
      {/* Bouton flottant */}
      {!open && (
        <Tooltip title="NetBot — Assistant IA réseau" placement="left">
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              width: 52, height: 52,
              bgcolor: currentMode.color,
              color: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,.25)',
              transition: 'all .2s',
              '&:hover': { transform: 'scale(1.08)', boxShadow: '0 6px 24px rgba(0,0,0,.3)' },
            }}
          >
            <SmartToyIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Panel chat */}
      <Collapse in={open} unmountOnExit>
        <Paper elevation={8} sx={{
          width: 380, height: 520,
          display: 'flex', flexDirection: 'column',
          borderRadius: 3, overflow: 'hidden',
          border: '1px solid', borderColor: 'divider',
        }}>

          {/* Header */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 2, py: 1.5,
            bgcolor: currentMode.color,
            color: '#fff',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon fontSize="small" />
              <Typography fontWeight={700} fontSize={14}>NetBot</Typography>
              {context && (
                <Chip
                  label="Lab actif"
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,.2)', color: '#fff', fontSize: 10, height: 18 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControl size="small" variant="standard">
                <Select
                  value={mode}
                  onChange={e => { setMode(e.target.value); setMessages([]); }}
                  sx={{
                    color: '#fff', fontSize: 11,
                    '&:before': { borderColor: 'rgba(255,255,255,.3)' },
                    '& .MuiSelect-icon': { color: '#fff' },
                  }}
                  disableUnderline={false}
                >
                  {MODES.map(m => (
                    <MenuItem key={m.value} value={m.value} sx={{ fontSize: 12 }}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Nouvelle conversation">
                <IconButton size="small" onClick={() => setMessages([])} sx={{ color: 'rgba(255,255,255,.8)' }}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#fff' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: '#f8fafc' }}>

            {/* Message de bienvenue */}
            {messages.length === 0 && (
              <Box sx={{
                textAlign: 'center', py: 2,
                color: 'text.secondary',
              }}>
                <SmartToyIcon sx={{ fontSize: 32, color: currentMode.color, mb: 1 }} />
                <Typography fontSize={13} fontWeight={600} color={currentMode.color}>
                  {mode === 'lab_debugger' && 'Debugger Lab GNS3'}
                  {mode === 'course_tutor' && 'Tuteur Réseau CCNA/CCNP'}
                  {mode === 'exam_assistant' && 'Assistant Examen'}
                </Typography>
                <Typography fontSize={11} color="text.secondary" mt={0.5}>
                  {mode === 'lab_debugger' && 'Je vois ta topologie et t\'aide à diagnostiquer.'}
                  {mode === 'course_tutor' && 'Je t\'explique les concepts sans faire à ta place.'}
                  {mode === 'exam_assistant' && 'Je te guide avec des indices sans donner les réponses.'}
                </Typography>
              </Box>
            )}

            {/* Suggestions rapides */}
            {showSugg && (
              <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center" mb={1.5}>
                {SUGGESTIONS[mode]?.map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    size="small"
                    onClick={() => send(s)}
                    sx={{
                      fontSize: 11, cursor: 'pointer',
                      bgcolor: '#fff',
                      border: `1px solid ${currentMode.color}30`,
                      color: currentMode.color,
                      '&:hover': { bgcolor: `${currentMode.color}10` },
                    }}
                  />
                ))}
              </Stack>
            )}

            {/* Messages */}
            {messages.map((m, i) => (
              <Box key={i} sx={{
                display: 'flex',
                justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 1.5, alignItems: 'flex-end', gap: 0.5,
              }}>
                {m.role === 'assistant' && (
                  <Box sx={{
                    width: 24, height: 24, borderRadius: '50%',
                    bgcolor: currentMode.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <SmartToyIcon sx={{ fontSize: 14, color: '#fff' }} />
                  </Box>
                )}
                <Box sx={{
                  maxWidth: '82%', px: 1.5, py: 1,
                  borderRadius: m.role === 'user'
                    ? '14px 14px 2px 14px'
                    : '14px 14px 14px 2px',
                  bgcolor: m.role === 'user'
                    ? currentMode.color
                    : (m.error ? '#fef2f2' : '#fff'),
                  color: m.role === 'user'
                    ? '#fff'
                    : (m.error ? '#dc2626' : '#1e293b'),
                  fontSize: 13, lineHeight: 1.6,
                  boxShadow: '0 1px 4px rgba(0,0,0,.08)',
                }}>
                  {m.role === 'assistant'
                    ? <MarkdownText content={m.content} />
                    : m.content
                  }
                </Box>
              </Box>
            ))}

            {loading && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pl: 4 }}>
                <Box sx={{ display: 'flex', gap: 0.4 }}>
                  {[0, 1, 2].map(i => (
                    <Box key={i} sx={{
                      width: 6, height: 6, borderRadius: '50%',
                      bgcolor: currentMode.color,
                      animation: 'pulse 1.2s ease-in-out infinite',
                      animationDelay: `${i * 0.2}s`,
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '50%':      { opacity: 1,   transform: 'scale(1.2)' },
                      },
                    }} />
                  ))}
                </Box>
                <Typography fontSize={11} color="text.secondary">NetBot réfléchit...</Typography>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Input */}
          <Box sx={{
            display: 'flex', gap: 1, p: 1.5,
            borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff',
          }}>
            <TextField
              fullWidth size="small"
              placeholder="Pose ta question réseau..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()}
              multiline maxRows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 } }}
            />
            <IconButton
              onClick={() => send()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: currentMode.color, color: '#fff', borderRadius: 2,
                '&:hover': { opacity: 0.9 },
                '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
